<script lang="ts">
import { Component, Vue } from 'vue-facing-decorator';
import {
    AnalyticsEventName,
    DOMUtil,
    GAEventAction,
    GAEventCategory,
    GTMUtils,
    IconName,
    LanguageCode,
    TranslationKey,
    TranslationService,
} from 'webshop-modules';
import Artist from './Artist.vue';
import SpinnerComponent from "@/components/atoms/Spinner.vue";
import SearchInputComponent from "@/components/molecules/SearchInput.vue";
import { useSearchResultsStore } from "@/domain/search/application/SearchResults.store";
import { ArtistStructured } from "@/domain/search/entitites/artists-structured.interface";
import { ArtistSearchResult } from 'webshop-api';

@Component({
    components: {
        Artist,
        SpinnerComponent,
        SearchInputComponent,
    },
})
export default class ArtistsSearchComponent extends Vue {
    public readonly IconName: typeof IconName = IconName;
    public readonly Translation: typeof TranslationKey = TranslationKey;

    public lang: LanguageCode = TranslationService.getLanguageCode();
    public searchLabel = '';

    private SearchResults = useSearchResultsStore();

    public get artists(): Array<ArtistSearchResult> {
        return this.SearchResults.artistsList.results ?? [];
    }

    public get artistsStructuredData(): ArtistStructured[] {
        const dataObject: ArtistStructured[] = [];

        if (this.artists) {
            for (const artist of this.artists) {
                if (artist.image) {
                    dataObject.push({
                        '@context': 'https://schema.org',
                        '@type': 'PerformingGroup',
                        name: artist.title,
                        url: artist.url,
                        image: DOMUtil.getWebshopStaticPathFrom(document) + artist.image,
                    });
                } else {
                    dataObject.push({
                        '@context': 'https://schema.org',
                        '@type': 'PerformingGroup',
                        name: artist.title,
                        url: artist.url,
                    });
                }
            }
        }

        return dataObject;
    }

    public get canLoadMore(): boolean {
        return this.SearchResults.haveMoreArtists && !this.SearchResults.inProgress;
    }

    public handlePushToDataLayer(e: Event): void {
        const form = e.target as HTMLFormElement;
        const artistsSearchInput = form.querySelector('[data-js=artists-search-input]') as HTMLInputElement;

        if (!artistsSearchInput) {
            return;
        }

        GTMUtils.pushCustomEventToDataLayer({
            category: GAEventCategory.Search,
            action: GAEventAction.ArtistSearch,
            label: artistsSearchInput.value,
        });

        GTMUtils.pushAnalyticsEventToDataLayer({
            event: AnalyticsEventName.SearchResults,
            search_term: artistsSearchInput.value,
        })

        e.preventDefault();
    }

    public loadMore(): void {
        this.SearchResults.artistsNextPage(this.$apiService);
    }

}
</script>

<template>
    <div id="artists-search-component" class="o-artistsSearchComponent">
        <div class="l-container l-bg-primary clearfix">
            <form @submit="handlePushToDataLayer($event)" data-js="artists-search-form">
                <SearchInputComponent
                    :label="searchLabel"
                    :artistSearch=true
                    :name="'artist-search'"
                    :icon="IconName.Search" />
            </form>
        </div>

        <component :is="'script'" type="application/ld+json" v-html="artistsStructuredData"></component>

        <template v-if="artists">
            <ul class="artists-search-results-list">
                <li v-for="artist in artists" :key="artist.url">
                    <Artist :item="artist"></Artist>
                </li>
            </ul>
        </template>

        <SpinnerComponent v-show="this.SearchResults.inProgress" label=""> </SpinnerComponent>

        <div class="m-searchList__loadMore" v-show="canLoadMore">
            <button
                class="a-btnCta -action"
                @click="loadMore"
                v-translate="{ key: Translation.More_search_results }"></button>
        </div>
    </div>
</template>
